import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import { MaintenanceService } from '../../services/maintenance.service';
import { EquipmentGroupDTO, mntLabel } from '../../interfaces/maintenance';

/** code -> name of every group loaded so far, so non-reactive helpers (report column defs) can show names. */
const nameCache = new Map<string, string>();

/** Display name of a group code: the institution's own name when known, else a prettified code. */
export const groupLabel = (code?: string | null): string => (code ? nameCache.get(code) ?? mntLabel(code) : '');

/** The institution's equipment groups (system defaults are created on first read by the server). */
export function useEquipmentGroups(institutionId: string) {
    const [groups, setGroups] = useState<EquipmentGroupDTO[]>([]);
    const [loaded, setLoaded] = useState(false);
    const toast = useToast();

    const reload = useCallback(async () => {
        // Retry once (the very first load can race another first load on the server), then tell the user
        // instead of silently showing an empty list.
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const list = await MaintenanceService.listGroups(institutionId);
                list.forEach(g => nameCache.set(g.code, g.name));
                setGroups(list);
                setLoaded(true);
                return;
            } catch {
                // fall through to the retry / the error toast
            }
        }
        toast.showError('Could not load the equipment groups. Reload the page to try again.');
        setLoaded(true);
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { reload(); }, [reload]);

    /** Active groups, plus the ones in `keep` (a row's current group stays pickable after deactivation). */
    const options = useCallback((...keep: (string | null | undefined)[]) =>
        groups.filter(g => g.active || keep.includes(g.code)), [groups]);

    return { groups, loaded, reload, options, label: groupLabel };
}
